import React, { useState } from "react";
import {
  Button,
  Modal,
  Alert,
  Space
} from "antd";
import {
  SendOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useSubmitPurchaseOrder } from "../hooks/useSubmitPurchaseOrder";
import WorkflowDiagram from "../../purchase-template/components/WorkflowDiagram";

// Component Props Interface
export interface SubmitButtonProps {
  orderId?: string;
  orderData?: any;
  order?: any;
  fiscalYear: number;
  buttonText?: string;
  buttonType?: "default" | "primary";
  icon?: React.ReactNode;
  onSuccess?: () => void;
  disabled?: boolean;
  size?: "small" | "middle" | "large";
}

export const SubmitButton: React.FC<SubmitButtonProps> = ({
  orderId,
  orderData,
  order,
  fiscalYear,
  buttonText = "Submit",
  buttonType = "primary",
  icon = <SendOutlined />,
  onSuccess,
  disabled = false,
  size = "middle"
}) => {
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);

  // Submit mutation hook
  const { submit: submitPurchaseOrder, isLoading } = useSubmitPurchaseOrder({
    onSuccess: () => {
      // Call the parent's onSuccess callback if provided
      onSuccess?.();
    },
    onError: (error) => {
      console.error('Submit error:', error);
    }
  });

  // Handle form submission
  const handleSubmit = async () => {
    setIsConfirmationModalVisible(true);
  };

  // Handle confirmation modal OK
  const handleConfirmationOk = async () => {
    setIsConfirmationModalVisible(false);
    try {
      // Proceed with actual submission
      if (orderId) {
        await submitPurchaseOrder({
          orderId: orderId,
          fiscalYear: fiscalYear,
        });
      } else if (orderData) {
        await submitPurchaseOrder({
          orderData: orderData,
          fiscalYear: fiscalYear,
        });
      }
    } catch (error) {
      console.error('Submission failed:', error);
    }
  };

  // Handle confirmation modal cancel
  const handleConfirmationCancel = () => {
    setIsConfirmationModalVisible(false);
  };

  return (
    <>
      {/* Submit Button */}
      <Button
        type={buttonType}
        onClick={handleSubmit}
        icon={icon}
        loading={isLoading}
        disabled={disabled}
        size={size}
      >
        {buttonText}
      </Button>

      {/* Confirmation Modal */}
      <Modal
        title={
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            Confirm Submission
          </Space>
        }
        open={isConfirmationModalVisible}
        onOk={handleConfirmationOk}
        onCancel={handleConfirmationCancel}
        okText="Submit"
        cancelText="Cancel"
        width={800}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="No Conflicts Found"
            description="Your purchase order can be submitted successfully."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>

        {/* Flow Diagram */}
        {order?.approvalType === 'EXTERNAL' && order?.externalWorkflow && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Approval Flow:</h4>
            <WorkflowDiagram flow={order.externalWorkflow} />
          </div>
        )}

        {order?.approvalType === 'LOCAL' && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Approval Flow:</h4>
            <Alert
              message="Local Approval Process"
              description="This order will follow the standard local approval workflow."
              type="info"
              showIcon
            />
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 8, fontWeight: 500 }}>
            By clicking "Submit", you confirm that:
          </p>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li style={{ marginBottom: 4 }}>
              The first approver will be notified immediately
            </li>
            <li style={{ marginBottom: 4 }}>
              The purchase order will be marked as submitted
            </li>
            <li style={{ marginBottom: 0 }}>
              You will no longer be able to edit this order
            </li>
          </ul>
        </div>
      </Modal>
    </>
  );
};

export default SubmitButton;